/**
 * ⟦C53 · A-170 §2⟧ **« La prise parfaite »** : le Tournage devient une jauge de jeu d'acteur.
 *
 * Matt, 01/10/2026 : *« je ne comprends pas pourquoi le rond et la grille des tiers ne sont pas
 * calés […] c'est trop horrible. On ne peut pas inventer un autre jeu ? »* Il a choisi la prise
 * parfaite : un curseur va et vient sur une jauge, une zone verte marque le moment juste, le joueur
 * appuie sur REC quand le curseur est dans le vert. Cinq prises par manche ; à chaque prise la zone
 * rétrécit et le curseur accélère.
 *
 * ## Pourquoi ce fichier est dans `shared/` et pas dans `server/`
 *
 * Le contrat de l'Atelier (SPEC-API §6.4) veut que le client n'envoie **que des gestes** et que le
 * serveur refasse le calcul. Ici le geste est un instant d'appui, et le calcul est la position du
 * curseur à cet instant : **une fonction déterministe du temps**, qui doit être *la même* des deux
 * côtés sous peine de montrer au joueur un curseur et de noter un autre. Plutôt que deux jumeaux qui
 * dérivent (c'était le cas des quatre autres jeux, `client/src/atelier/<jeu>/`), un seul module pur,
 * importé par le serveur (pattern, barème, plausibilité) et par le client (jauge, bac à sable).
 *
 * Tout est **pur** : aucune I/O, aucune horloge, aucun `Math.random`. Le seul aléa est le
 * `Tireur` qu'on passe au générateur, le `RngPattern` seedé du serveur, ou un tirage `draw` en
 * bac à sable.
 *
 * ## Le temps d'une prise
 *
 * `t = 0` à l'instant où le curseur commence à bouger (la fin du clap). Le client mesure l'appui en
 * millisecondes depuis cet instant ; la prise n'a pas d'horloge commune avec le serveur, donc ce
 * temps *local* est la seule chose qui puisse voyager.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Constantes ✎ (à régler en jouant), mesurées par `server/src/atelier/__tests__/mesure-c53.ts`
// ─────────────────────────────────────────────────────────────────────────────
/** Cinq prises par manche : c'est le jeu entier. */
export const PRISES_PAR_MANCHE = 5;
/**
 * En deçà, un appui n'est pas un geste : on n'a pas le temps de réagir au clap. Le **client ne
 * l'envoie pas** (il l'ignore) et le **serveur** y voit une trace fabriquée (`appui_trop_precoce`).
 * Les deux lisent cette constante, c'est ce qui garantit qu'un joueur honnête n'est jamais flaggé
 * pour avoir tapé trop vite.
 */
export const APPUI_MIN_MS = 150;
/** Points d'un appui pile au bord de la zone verte ; le centre en vaut 100. */
export const POINTS_BORD = 50;
/** Une prise « en or » (zone dorée touchée) : au-delà de 100, c'est un bonus qui rattrape les ratés. */
export const POINTS_DOREE = 110;
/** Part du demi-rayon vert, au centre, comptée comme parfaite (100 plein) : un humain peut la viser. */
export const PARFAITE_PART = 0.1;
/** Demi-largeur de la zone dorée, en part du demi-rayon vert (variante `doree`). */
export const OR_PART = 0.3;
/** *Intro signature* : la première prise vaut au moins ça, quoi qu'il arrive. */
export const PRISE_INTRO_PLANCHER = 70;
/** On ne peut jamais effacer plus de ratés que ça : il faut que des prises comptent. */
export const PARDONS_MAX = 3;
/** *Le plan lumière* (⟦A-56⟧ devenu un simple avantage) : la zone verte s'élargit de ce facteur. */
export const ECLAIRAGE_ZONE_MULT = 1.12;
/** Tolérance d'accessibilité (SPEC-UI §7.3) : les zones, ×1,30, comme toutes les fenêtres. */
export const TOLERANCE_ZONE_MULT = 1.3;
/** Le mode posé (⟦A-83⟧ §7.4) ralentit le curseur de ce facteur et ne sert aucune variante. */
export const POSE_VITESSE_MULT = 0.6;
/** Plafond de la demi-largeur verte : même tout cumulé, la jauge reste un jeu. */
export const ZONE_DEMI_MAX = 0.3;
/** Traversées de jauge par seconde, à la prise 1, par palier E0..E5 (1 traversée = de 0 à 1). */
export const PRISE_VITESSE = [0.45, 0.52, 0.57, 0.62, 0.67, 0.72];
/** Demi-largeur de la zone verte à la prise 1, en fraction de la jauge, par palier E0..E5. */
export const PRISE_ZONE_DEMI = [0.15, 0.13, 0.115, 0.105, 0.098, 0.092];
/** « À chaque prise, le curseur accélère » : +10 % de vitesse par prise de la manche. */
export const PRISE_ACCELERATION = 0.1;
/** « … et la zone rétrécit » : −9 % de demi-largeur par prise de la manche. */
export const PRISE_RETRECISSEMENT = 0.09;
/**
 * Chance qu'une prise (hors la première) serve une variante, par palier E0..E5, interpolée vers le
 * palier suivant à mesure que la chaîne avance dedans. **E0 = 0 : le mode doux n'en sert jamais.**
 */
export const PRISE_VARIANTE_PROBA = [0, 0.25, 0.35, 0.45, 0.5, 0.55];
/**
 * Les variantes qui entrent en jeu à chaque palier : on commence par les gentilles (une zone qui
 * glisse, une zone dorée), l'éternuement arrive à E2, le curseur qui change de vitesse à E3.
 */
export const VARIANTES_PAR_NIVEAU = [
    [],
    ["doree", "mobile"],
    ["doree", "mobile", "eternuement"],
    ["doree", "mobile", "eternuement", "vitesse"],
    ["doree", "mobile", "eternuement", "vitesse"],
    ["doree", "mobile", "eternuement", "vitesse"],
];
// Réglages des variantes ✎
/** `mobile` : amplitude de glissement du centre de la zone (fraction de jauge) et sa pulsation (rad/s). */
export const MOBILE_AMPLITUDE = [0.1, 0.17];
export const MOBILE_PULSATION = [0.9, 1.6];
/** `vitesse` : le curseur accélère (sprint) ou freine (pause) pendant ce temps (ms), puis reprend. */
export const VITESSE_PHASE_MS = [900, 1600];
export const VITESSE_SPRINT = [1.5, 1.85];
export const VITESSE_PAUSE = [0.5, 0.62];
/** Un passage dans la zone ne descend jamais sous cette durée, même en plein sprint (ms). */
export const PASSAGE_MIN_MS = 75;
/** `eternuement` : l'acteur renifle puis éternue, la fenêtre interdite déborde du passage de ± ça (ms). */
export const ETERNUEMENT_MARGE_MS = 250;
/** Part du début de la fenêtre passée à renifler (l'annonce) avant l'éternuement lui-même. */
export const ETERNUEMENT_SNIF_PART = 0.45;
/**
 * Le premier passage du curseur dans la zone tombe entre ces bornes (ms) : le temps de comprendre.
 * Le plancher est ce qu'une zone centrale et un curseur vif permettent au pire (à E5, prise 5 :
 * `(1 − 2 × demi) / vitesse` ≈ 0,84 s), pas plus haut, sinon certaines prises n'auraient aucun départ valable.
 */
export const PREMIER_PASSAGE_MS = [600, 2600];
/** Durée d'une prise : ≈ 1,3 aller-retour du curseur, bornée (ms). */
export const DUREE_MIN_MS = 3600;
export const DUREE_MAX_MS = 6400;
/** Garde-fou de l'extension de durée quand il faut laisser un passage après l'éternuement (ms). */
export const DUREE_EXTENSION_MAX_MS = 9000;
// ─────────────────────────────────────────────────────────────────────────────
// La fonction du temps : LE cœur partagé
// ─────────────────────────────────────────────────────────────────────────────
const borne = (v, min, max) => Math.max(min, Math.min(max, v));
/** L'onde triangle de période 2 : 0 → 1 → 0, continue. `x` est en traversées. */
export function triangle(x) {
    const m = ((x % 2) + 2) % 2;
    return m <= 1 ? m : 2 - m;
}
/**
 * Le « temps du curseur », en secondes : `∫ mult(s) ds` de 0 à `tMs`. Sans `rythmes`, c'est
 * `tMs / 1000`. Avec, la phase reste **continue** (le curseur ne saute jamais), seule sa vitesse
 * change. Fermée et sans état : serveur et client la calculent identiquement pour n'importe quel
 * `tMs`, dans n'importe quel ordre.
 */
export function phaseCurseur(rythmes, tMs) {
    if (!rythmes || rythmes.length === 0)
        return tMs / 1000;
    let phase = 0;
    let depuis = 0;
    let mult = 1;
    for (const r of rythmes) {
        if (tMs <= r.atMs)
            break;
        phase += (r.atMs - depuis) * mult;
        depuis = r.atMs;
        mult = r.mult;
    }
    phase += (tMs - depuis) * mult;
    return phase / 1000;
}
/** La cadence du curseur à `tMs` : le multiplicateur en vigueur (1 = la vitesse de la prise). */
export function cadenceA(rythmes, tMs) {
    let mult = 1;
    for (const r of rythmes ?? []) {
        if (tMs < r.atMs)
            break;
        mult = r.mult;
    }
    return mult;
}
/** **La position du curseur à `tMs`**, fraction de jauge dans `[0 ; 1]`. Déterministe. */
export function positionCurseur(prise, tMs) {
    return triangle(prise.depart + prise.vitesse * phaseCurseur(prise.rythmes, tMs));
}
/** Le centre de la zone verte à `tMs` (elle glisse dans la variante `mobile`). */
export function centreZoneA(prise, tMs) {
    const z = prise.zone;
    if (!z.mobile)
        return z.centre;
    return z.centre + z.mobile.amplitude * Math.sin(z.mobile.pulsation * (tMs / 1000) + z.mobile.phase);
}
/** Où en est l'éternuement à `tMs` : rien, le snif qui l'annonce, ou l'éternuement lui-même. */
export function etatEternuement(prise, tMs) {
    const e = prise.eternuement;
    if (!e || tMs < e.debutMs || tMs > e.finMs)
        return "aucun";
    return tMs < e.debutMs + (e.finMs - e.debutMs) * ETERNUEMENT_SNIF_PART ? "snif" : "atchoum";
}
// ─────────────────────────────────────────────────────────────────────────────
// Le barème
// ─────────────────────────────────────────────────────────────────────────────
const rate = (issue, ecart = null, position = null) => ({
    issue,
    points: 0,
    ecart,
    position,
});
/**
 * **Juge un appui** : où était le curseur à `atMs`, et dans quoi il se trouvait.
 *
 * `atMs` absent (`null`/`undefined`) = la prise est allée au bout sans appui. L'ordre des
 * verdicts est celui d'un plateau : trop tôt, trop tard, l'éternuement, puis le curseur.
 *
 * ```
 * hors du vert      : 0
 * dans le vert      : 50 (au bord) → 100 (au centre)   · linéaire, 100 plein dans la part « parfaite »
 * dans l'or (doree) : 110, le bonus qui peut rattraper un raté, plafonné à 100 sur la manche
 * ```
 */
export function evaluerAppui(prise, atMs) {
    if (atMs === null || atMs === undefined || !Number.isFinite(atMs))
        return rate("sans_appui");
    if (atMs < APPUI_MIN_MS)
        return rate("trop_tot");
    if (atMs > prise.dureeMs)
        return rate("sans_appui");
    const e = prise.eternuement;
    if (e && atMs >= e.debutMs && atMs <= e.finMs)
        return rate("eternuement");
    const position = positionCurseur(prise, atMs);
    const centre = centreZoneA(prise, atMs);
    const demi = prise.zone.demi;
    const d = Math.abs(position - centre);
    if (d > demi)
        return rate("hors_zone", d / demi, position);
    const ecart = d / demi;
    if (prise.or && d <= prise.or.demi) {
        return { issue: "doree", points: POINTS_DOREE, ecart, position };
    }
    if (ecart <= PARFAITE_PART)
        return { issue: "parfaite", points: 100, ecart, position };
    const q = 1 - ecart;
    return { issue: "bonne", points: Math.round(POINTS_BORD + (100 - POINTS_BORD) * q), ecart, position };
}
/**
 * **La note d'une manche**, 0-100 : la moyenne des points de ses prises (un raté en vaut 0),
 * plafonnée à 100, les prises en or peuvent donc rattraper un raté, sans jamais dépasser l'échelle.
 *
 * `appuis[i]` est l'instant d'appui de la prise `i` (ms depuis le début du curseur), ou `null` /
 * absent si elle est allée au bout sans appui.
 *
 * Deux avantages de compétence, **neutres à 0** (`pardons: 0`, `premiereAssuree: 0` : la formule
 * ci-dessous rend alors exactement la moyenne brute) :
 * - `pardons` : les N prises ratées ne comptent pas dans la moyenne (la caméra de secours) ;
 * - `premiereAssuree` : la première prise vaut au moins ce plancher (la signature de l'intro).
 */
export function noterManche(prises, appuis, options = {}) {
    const resultats = prises.map((p, i) => evaluerAppui(p, appuis[i]));
    const plancher = options.premiereAssuree ?? 0;
    if (plancher > 0 && resultats[0] && resultats[0].points < plancher) {
        resultats[0] = { ...resultats[0], points: plancher };
    }
    const pardonnees = [];
    const pardons = Math.max(0, Math.min(PARDONS_MAX, Math.trunc(options.pardons ?? 0)));
    if (pardons > 0) {
        for (let i = 0; i < resultats.length && pardonnees.length < pardons; i++) {
            if (resultats[i].points === 0)
                pardonnees.push(i);
        }
    }
    const comptees = resultats.filter((_, i) => !pardonnees.includes(i));
    const moyenne = comptees.length === 0 ? 0 : comptees.reduce((somme, r) => somme + r.points, 0) / comptees.length;
    return {
        score: Math.round(borne(moyenne, 0, 100)),
        moyenne,
        prises: resultats,
        pardonnees,
    };
}
/**
 * Les intervalles où le curseur est dans la zone verte, de `0` à `jusquaMs`, échantillonnés tous
 * les `pasMs`. C'est ce qui décide du premier passage, de la fenêtre d'éternuement, et ce que
 * les tests (et le générateur) utilisent pour **prouver** qu'une prise se réussit.
 */
export function passagesDeLaZone(prise, pasMs = 5, jusquaMs = prise.dureeMs) {
    const passages = [];
    let debut = null;
    for (let t = 0; t <= jusquaMs; t += pasMs) {
        const dedans = Math.abs(positionCurseur(prise, t) - centreZoneA(prise, t)) <= prise.zone.demi;
        if (dedans && debut === null)
            debut = t;
        if (!dedans && debut !== null) {
            passages.push({ debutMs: debut, finMs: t - pasMs });
            debut = null;
        }
    }
    if (debut !== null)
        passages.push({ debutMs: debut, finMs: jusquaMs });
    return passages;
}
/**
 * Les passages **jouables** : dans le vert, après le délai de réaction, avant la fin de la prise,
 * hors de la fenêtre d'éternuement, et assez longs (`dureeMinMs`) pour qu'un humain y tienne.
 * Une prise est **réussissable** si et seulement si cette liste n'est pas vide.
 */
export function passagesJouables(prise, dureeMinMs = 40) {
    const e = prise.eternuement;
    const sortie = [];
    for (const p of passagesDeLaZone(prise)) {
        let segments = [
            { debutMs: Math.max(p.debutMs, APPUI_MIN_MS), finMs: Math.min(p.finMs, prise.dureeMs) },
        ];
        if (e) {
            segments = segments.flatMap((s) => {
                if (e.finMs < s.debutMs || e.debutMs > s.finMs)
                    return [s];
                const morceaux = [];
                if (e.debutMs > s.debutMs)
                    morceaux.push({ debutMs: s.debutMs, finMs: e.debutMs });
                if (e.finMs < s.finMs)
                    morceaux.push({ debutMs: e.finMs, finMs: s.finMs });
                return morceaux;
            });
        }
        for (const s of segments)
            if (s.finMs - s.debutMs >= dureeMinMs)
                sortie.push(s);
    }
    return sortie;
}
// ─────────────────────────────────────────────────────────────────────────────
// Les réglages d'une manche, et leur interpolation entre deux paliers
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Une table indexée par palier E0..E5, **tirée vers le palier suivant** de `avancement` (`f`, la
 * progression de la chaîne dans son palier). Sans avancement (`f = 0`, E0, E5), c'est la colonne de
 * `E` telle quelle, même règle que `valeurProgressive` du serveur (⟦C47⟧).
 */
export function parNiveau(table, niveau, avancement = 0) {
    const i = borne(Math.trunc(niveau), 0, table.length - 1);
    const a = table[i];
    const b = table[Math.min(table.length - 1, i + 1)] ?? a;
    return a + (b - a) * borne(avancement, 0, 1);
}
/**
 * Les réglages d'une manche au palier `niveau` (E, 0 à 5) et à l'avancement `avancement` dans ce
 * palier. **Sans option, c'est le jeu de base du palier** : tolérance, assists, éclairage et mode
 * posé sont des facteurs qui valent 1 quand ils n'existent pas, l'état neutre prouvé par
 * l'arithmétique.
 */
export function reglagesPrises(niveau, avancement = 0, options = {}) {
    const E = borne(Math.trunc(niveau), 0, PRISE_VITESSE.length - 1);
    const zoneMult = (options.tolerance ? TOLERANCE_ZONE_MULT : 1) *
        (options.zoneMult ?? 1) *
        (options.eclairage ? ECLAIRAGE_ZONE_MULT : 1);
    const pose = options.pose === true;
    return {
        prises: PRISES_PAR_MANCHE,
        vitesse: parNiveau(PRISE_VITESSE, E, avancement) * (pose ? POSE_VITESSE_MULT : 1),
        zoneDemi: Math.min(ZONE_DEMI_MAX, parNiveau(PRISE_ZONE_DEMI, E, avancement) * zoneMult),
        acceleration: PRISE_ACCELERATION,
        retrecissement: PRISE_RETRECISSEMENT,
        probaVariante: pose ? 0 : parNiveau(PRISE_VARIANTE_PROBA, E, avancement),
        variantes: pose ? [] : (VARIANTES_PAR_NIVEAU[E] ?? []),
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Le générateur
// ─────────────────────────────────────────────────────────────────────────────
const entre = (rng, [min, max]) => min + rng.next() * (max - min);
/**
 * Génère les prises d'une manche. **Déterministe** : mêmes `reglages` et même suite du `Tireur` ⇒
 * mêmes prises (c'est ce qui permet au serveur de régénérer, pour noter, la manche que le joueur a
 * eue sous les doigts).
 *
 * Garanties (chacune a son test) :
 * 1. **Chaque prise se réussit.** Il existe toujours un moment où le curseur est dans le vert, hors
 *    éternuement, après le délai de réaction (`passagesJouables`).
 * 2. **Le premier passage n'est jamais immédiat** : entre 0,9 s et 2,6 s après le clap.
 * 3. **La difficulté monte dans la manche** : la zone rétrécit et le curseur accélère à chaque prise.
 * 4. **La première prise est toujours le jeu de base** : on apprend avant de se faire surprendre.
 * 5. **L'éternuement tombe sur un passage** (sinon il ne gênerait personne) **et laisse un passage
 *    derrière lui** (sinon il serait injuste).
 */
export function genererPrises(rng, reglages) {
    const prises = [];
    for (let rang = 0; rang < reglages.prises; rang++) {
        const vitesse = reglages.vitesse * (1 + reglages.acceleration * rang);
        const demi = reglages.zoneDemi * (1 - reglages.retrecissement * rang);
        // La variante : deux tirages toujours consommés, pour que la suite reste lisible d'une prise à l'autre.
        const uVariante = rng.next();
        const uType = rng.next();
        const tirables = reglages.variantes;
        const variante = rang >= 1 && tirables.length > 0 && uVariante < reglages.probaVariante
            ? tirables[Math.min(tirables.length - 1, Math.floor(uType * tirables.length))]
            : "classique";
        const amplitude = variante === "mobile" ? entre(rng, MOBILE_AMPLITUDE) : 0;
        const pulsation = variante === "mobile" ? entre(rng, MOBILE_PULSATION) : 0;
        const phaseMobile = rng.next() * Math.PI * 2;
        const marge = demi + 0.05 + amplitude;
        const centre = marge + rng.next() * Math.max(0, 1 - 2 * marge);
        // La durée : environ 1,3 aller-retour du curseur, bornée.
        let dureeMs = Math.round(borne(((1.3 * 2) / vitesse) * 1000, DUREE_MIN_MS, DUREE_MAX_MS) / 100) * 100;
        const prise = {
            rang,
            variante,
            dureeMs,
            depart: 0,
            vitesse,
            zone: {
                centre,
                demi,
                ...(variante === "mobile" ? { mobile: { amplitude, pulsation, phase: phaseMobile } } : {}),
            },
            ...(variante === "doree" ? { or: { demi: demi * OR_PART } } : {}),
        };
        if (variante === "vitesse") {
            const rythmes = genererRythmes(rng, prise);
            if (rythmes.length > 0)
                prise.rythmes = rythmes;
        }
        // Le départ : le premier passage dans la zone ne doit être ni immédiat ni lointain.
        prise.depart = choisirDepart(rng, prise);
        if (variante === "eternuement") {
            prise.eternuement = fenetreEternuement(prise);
            // Il faut un passage jouable APRÈS l'éternuement : on allonge la prise tant que ce n'est pas le cas.
            let allonge = prise.dureeMs;
            while (passagesJouables({ ...prise, dureeMs: allonge }).length === 0 && allonge < DUREE_EXTENSION_MAX_MS) {
                allonge += 500;
            }
            dureeMs = allonge;
            prise.dureeMs = dureeMs;
        }
        prises.push(prise);
    }
    return prises;
}
/** Les changements de cadence de la variante `vitesse` : un sprint ou une pause, puis le retour à la normale. */
function genererRythmes(rng, prise) {
    const { vitesse, zone } = prise;
    // Un sprint ne ramène jamais le passage dans la zone sous `PASSAGE_MIN_MS`.
    const sprintMax = (2 * zone.demi) / (vitesse * (PASSAGE_MIN_MS / 1000));
    const rythmes = [];
    let t = 600 + rng.next() * 700;
    for (let i = 0; i < 2; i++) {
        const sprint = i % 2 === 0;
        const mult = sprint
            ? Math.min(entre(rng, VITESSE_SPRINT), sprintMax)
            : entre(rng, VITESSE_PAUSE);
        const dure = entre(rng, VITESSE_PHASE_MS);
        if (t + dure > prise.dureeMs - 500)
            break;
        // Un sprint sous 1,15 n'est plus un sprint : on le laisse tomber plutôt que de mentir.
        if (sprint && mult < 1.15) {
            t += dure;
            continue;
        }
        rythmes.push({ atMs: Math.round(t), mult: Math.round(mult * 100) / 100 });
        rythmes.push({ atMs: Math.round(t + dure), mult: 1 });
        t += dure + 500 + rng.next() * 500;
    }
    return rythmes;
}
/**
 * Choisit la phase de départ du curseur : la plus aléatoire possible parmi celles qui font tomber
 * le premier passage dans `PREMIER_PASSAGE_MS`. Rejet sur quelques tirages, puis balayage
 * déterministe de tous les départs si le hasard n'a rien donné ; si aucun ne convient (une zone
 * centrale et un curseur très vif), on prend celui qui retarde le plus le premier passage.
 */
function choisirDepart(rng, prise) {
    const [min, max] = PREMIER_PASSAGE_MS;
    const premierPassage = (depart) => passagesDeLaZone({ ...prise, depart }, 10, max + 200)[0]?.debutMs ?? Number.POSITIVE_INFINITY;
    const accepte = (debut) => debut >= min && debut <= max;
    for (let essai = 0; essai < 24; essai++) {
        const depart = rng.next() * 2;
        if (accepte(premierPassage(depart)))
            return depart;
    }
    const origine = rng.next() * 2;
    let meilleur = { depart: origine, debut: -1 };
    for (let pas = 0; pas < 400; pas++) {
        const depart = (origine + pas * 0.005) % 2;
        const debut = premierPassage(depart);
        if (accepte(debut))
            return depart;
        // Aucun ne convient : on retient le départ qui laisse le plus de temps avant le premier passage.
        if (debut < Number.POSITIVE_INFINITY && debut > meilleur.debut)
            meilleur = { depart, debut };
    }
    return meilleur.depart;
}
/** La fenêtre d'éternuement : elle recouvre le PREMIER passage du curseur dans la zone, marge comprise. */
function fenetreEternuement(prise) {
    const premier = passagesDeLaZone(prise)[0];
    const debut = premier?.debutMs ?? 1000;
    const fin = premier?.finMs ?? 1500;
    return {
        debutMs: Math.max(APPUI_MIN_MS + 100, Math.round(debut - ETERNUEMENT_MARGE_MS)),
        finMs: Math.round(fin + ETERNUEMENT_MARGE_MS),
    };
}
//# sourceMappingURL=prise-parfaite.js.map